import { getFirebase } from '../../firebase/lazy';
import { clearAuthTokens } from '../../utils/authToken';
import logger from '../../utils/logger.js';
import { API_BASE_URL } from '../../config/api.config';

// Enhanced auth flow logging
const logAuthEvent = (event, data = {}) => {
  logger.info(`[Firebase Auth] ${event}`, {
    ...data,
    apiBaseUrl: API_BASE_URL,
    timestamp: new Date().toISOString(),
  }, ['auth', 'firebase']);
};

const storeAuthToken = (token) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('vh_auth_token', token);
  localStorage.setItem('access_token', token);
  localStorage.setItem('vh_auth_strategy', 'firebase');
};

const storeUserData = (userData) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('vh_user_data', JSON.stringify(userData));
};

/** Align with subscribe(): derive organizationId string from /sync-firebase-profile `data` */
function organizationIdFromSyncPayload(syncData) {
  if (!syncData || typeof syncData !== 'object') return null;
  const direct = syncData.organizationId;
  if (direct != null) {
    if (typeof direct === 'object') {
      return (direct._id ?? direct.id)?.toString?.() ?? null;
    }
    return String(direct);
  }
  const org = syncData.organization;
  if (org != null && typeof org === 'object') {
    return (org._id ?? org.id)?.toString?.() ?? null;
  }
  return null;
}

const FIREBASE_REGISTRATION_ERRORS = {
  'auth/email-already-in-use': {
    status: 409,
    field: 'email',
    message: 'An account with this email already exists.',
  },
  'auth/invalid-email': {
    status: 400,
    field: 'email',
    message: 'Please enter a valid email address.',
  },
  'auth/weak-password': {
    status: 400,
    field: 'password',
    message: 'Please choose a stronger password.',
  },
};

function normalizeRegistrationError(error, statusOverride) {
  if (error?.status && Array.isArray(error?.errors) && error?.message) return error;

  const firebaseDetails = FIREBASE_REGISTRATION_ERRORS[error?.code];
  const status = statusOverride ?? error?.status ?? firebaseDetails?.status ?? 400;
  const message = firebaseDetails?.message || error?.message || 'Registration failed. Please try again.';
  const errors = Array.isArray(error?.errors)
    ? error.errors
    : firebaseDetails?.field
      ? [{ field: firebaseDetails.field, message }]
      : [];

  return Object.assign(new Error(message), {
    status,
    errors,
    ...(error?.code && { code: error.code }),
  });
}

function deriveOrganizationId(apiUser) {
  if (apiUser?.organizationId != null) {
    if (typeof apiUser.organizationId === 'object' && apiUser.organizationId != null) {
      return (apiUser.organizationId._id ?? apiUser.organizationId.id)?.toString();
    }
    return String(apiUser.organizationId);
  }
  return (apiUser?.organization?._id ?? apiUser?.organization?.id)?.toString?.() ?? null;
}

let activeAuthCallbacks = null;

const firebaseAdapter = {
  signIn: async (email, password) => {
    logAuthEvent('signIn:start', { email });
    try {
      const { auth, signInWithEmailAndPassword } = await getFirebase();
      logAuthEvent('signIn:firebase_loaded');
      const result = await signInWithEmailAndPassword(auth, email, password);
      logAuthEvent('signIn:success', { uid: result.user?.uid, email: result.user?.email });
      return result;
    } catch (error) {
      logAuthEvent('signIn:error', { email, errorCode: error.code, errorMessage: error.message });
      throw error;
    }
  },

  signOut: async () => {
    const { auth, signOut } = await getFirebase();
    await signOut(auth);
    clearAuthTokens();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('vh_auth_strategy');
    }
  },

  register: async (email, password, userData = {}) => {
    try {
      const { auth, createUserWithEmailAndPassword, db, doc, setDoc, updateProfile } = await getFirebase();

      // Create user account in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update Firebase profile with display name
      const displayName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
      if (displayName) {
        await updateProfile(user, { displayName });
      }

      // Determine role and accountType
      const rawRole = (userData.role || userData.organizationType || '').toLowerCase();
      let role = 'worker';
      let accountType = userData.accountType || 'worker';
      
      if (['gc', 'gcadmin', 'contractor', 'general', 'general-contractor'].includes(rawRole)) {
        role = 'gcAdmin';
        accountType = 'organization';
      } else if (['sc', 'scadmin', 'subcontractor', 'sub-contractor'].includes(rawRole)) {
        role = 'scAdmin';
        accountType = 'organization';
      } else if (['foreman'].includes(rawRole)) {
        role = 'foreman';
        accountType = 'foreman';
      } else if (['safety_supervisor', 'safetysupervisor', 'safety-supervisor'].includes(rawRole)) {
        role = 'safety_supervisor';
        accountType = 'safety_supervisor';
      } else if (['worker'].includes(rawRole)) {
        role = 'worker';
        accountType = 'worker';
      }

      // Prepare user data for Firestore
      const userDocData = {
        email: user.email,
        role: role,
        accountType: accountType,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        organizationName: userData.organizationName || '',
        organizationType: userData.organizationType || '',
        inviteCode: userData.inviteCode || '',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save user data to Firestore
      await setDoc(doc(db, 'users', user.uid), userDocData);

      // Sync user to MongoDB backend by calling /api/auth/sync-firebase-profile
      // For organization signups this is BLOCKING — user must have correct role + org before entering the app
      const isOrgSignup = accountType === 'organization' || accountType === 'sub_contractor';
      let syncSucceeded = false;
      let syncPayload = null;
      let syncFailure = null;

      try {
        const idToken = await user.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/auth/sync-firebase-profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            accountType: accountType,
            role: role,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            phone: userData.phone || '',
            organizationName: userData.organizationName || '',
            organizationType: userData.organizationType || '',
          }),
        });
        if (response.ok) {
          logger.info('Successfully synced Firebase user to MongoDB backend');
          syncSucceeded = true;
          try {
            const body = await response.json();
            syncPayload = body?.data ?? null;
          } catch (parseErr) {
            logger.warn('sync-firebase-profile: could not parse response JSON', {
              error: parseErr?.message,
            });
          }
        } else {
          logger.error('Failed to sync user to MongoDB backend', { status: response.status });
          let errorBody = {};
          try {
            errorBody = await response.json();
          } catch (_parseError) {
            errorBody = {};
          }
          syncFailure = normalizeRegistrationError(errorBody, response.status);
        }
      } catch (syncError) {
        logger.error('Error syncing user to MongoDB backend:', { error: syncError });
        syncFailure = normalizeRegistrationError(syncError);
      }

      // For org signups, sync MUST succeed — otherwise delete Firebase user and fail registration
      if (isOrgSignup && !syncSucceeded) {
        logger.error('Organization signup failed: backend sync did not complete. Cleaning up Firebase user.');
        try {
          // Delete the just-created Firebase user (client SDK allows self-deletion)
          await user.delete();
          logger.info('Firebase user deleted after failed org sync');
        } catch (deleteErr) {
          // Fallback: sign out if delete fails
          logger.warn('Could not delete Firebase user, signing out instead:', { error: deleteErr.message });
          const { auth: fbAuth, signOut: fbSignOut } = await getFirebase();
          await fbSignOut(fbAuth);
        }
        clearAuthTokens();
        throw syncFailure || normalizeRegistrationError(
          new Error('Registration failed — please try again. If the problem persists, contact support.')
        );
      }

      const organizationId = organizationIdFromSyncPayload(syncPayload);
      const mergedFromSync =
        syncPayload != null
          ? {
              ...(syncPayload.role != null && { role: syncPayload.role }),
              ...(syncPayload.firstName != null && { firstName: syncPayload.firstName }),
              ...(syncPayload.lastName != null && { lastName: syncPayload.lastName }),
              ...(syncPayload.accountType != null && { accountType: syncPayload.accountType }),
              ...(organizationId != null && { organizationId }),
              ...(syncPayload.organization != null && { organization: syncPayload.organization }),
            }
          : {};

      // Return user object with Firestore + backend sync fields so AuthContext matches /me before onIdTokenChanged runs
      return {
        ...user,
        ...userDocData,
        ...mergedFromSync,
      };
    } catch (error) {
      logger.error('Firebase registration error:', { error: error });
      throw normalizeRegistrationError(error);
    }
  },

  subscribe: (setUser, setRoles) => {
    let unsubscribe = () => {};
    let refreshIntervalId = null;
    activeAuthCallbacks = { setUser, setRoles };
    logAuthEvent('subscribe:initializing');

    // Lazy load Firebase auth and set up listener
    getFirebase().then(({ auth }) => {
      logAuthEvent('subscribe:firebase_loaded');
      unsubscribe = auth.onIdTokenChanged(async (firebaseUser) => {
        if (firebaseUser) {
          logAuthEvent('subscribe:user_detected', { uid: firebaseUser.uid, email: firebaseUser.email });
          try {
            const idToken = await firebaseUser.getIdToken();
            logAuthEvent('subscribe:token_obtained', { tokenLength: idToken?.length });
            storeAuthToken(idToken);
            logAuthEvent('subscribe:token_stored', {
              tokenStored: typeof localStorage !== 'undefined' && !!localStorage.getItem('vh_auth_token'),
              tokenLength: idToken?.length,
            });

            // Periodic token refresh (ID tokens expire in ~1 hour)
            if (refreshIntervalId) clearInterval(refreshIntervalId);
            refreshIntervalId = setInterval(async () => {
              try {
                const token = await firebaseUser.getIdToken(true);
                storeAuthToken(token);
                logAuthEvent('subscribe:token_refreshed', { tokenLength: token?.length });
              } catch (err) {
                logger.warn('[Firebase Auth] Token refresh failed', { error: err?.message });
              }
            }, 55 * 60 * 1000);

            let userData = null;
            try {
              const { db, doc, getDoc } = await getFirebase();
              const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
              userData = userDoc.data();
              logAuthEvent('subscribe:firestore_user', { hasData: !!userData, role: userData?.role });
            } catch (error) {
              logger.warn('Error fetching Firebase user profile:', { error });
            }

            let apiUser = null;
            try {
              logAuthEvent('subscribe:calling_api_auth_me', {
                url: `${API_BASE_URL}/api/auth/me`,
                hasAuthorization: !!idToken,
                tokenLength: idToken?.length,
              });
              const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
                headers: {
                  Authorization: `Bearer ${idToken}`,
                },
              });
              logAuthEvent('subscribe:api_auth_me_response', { status: response.status, ok: response.ok });
              if (response.ok) {
                const data = await response.json();
                apiUser = data?.data || null;
                logAuthEvent('subscribe:api_user_data', {
                  hasUser: !!apiUser,
                  role: apiUser?.role,
                  organizationId: apiUser?.organizationId,
                  email: apiUser?.email
                });
              } else {
                const errorText = await response.text();
                logAuthEvent('subscribe:api_auth_me_error', { status: response.status, error: errorText });
              }
            } catch (error) {
              logger.warn('Error fetching backend user profile:', { error: error.message });
            }

            const role = apiUser?.role || userData?.role || userData?.organizationType || 'gc';
            const roles = apiUser?.roles || userData?.roles || [role];
            logAuthEvent('subscribe:resolved_role', { role, roles, source: apiUser?.role ? 'api' : (userData?.role ? 'firestore' : 'default') });

            // Derive organizationId as string for context and localStorage (API may return organization as populated object)
            const organizationId = deriveOrganizationId(apiUser);

            const mergedUser = {
              ...firebaseUser,
              ...userData,
              ...apiUser,
              role,
              firebaseUid: firebaseUser.uid,
              ...(organizationId != null && { organizationId }),
            };

            logAuthEvent('subscribe:user_merged', {
              email: mergedUser.email,
              role: mergedUser.role,
              organizationId: mergedUser.organizationId,
              hasApiData: !!apiUser,
              hasFirestoreData: !!userData
            });

            setUser(mergedUser);
            setRoles(roles);
            storeUserData({
              ...userData,
              ...apiUser,
              uid: apiUser?.uid || firebaseUser.uid,
              firebaseUid: firebaseUser.uid,
              email: firebaseUser.email,
              role,
              ...(organizationId != null && { organizationId }),
            });
            logAuthEvent('subscribe:complete', { email: mergedUser.email, role: mergedUser.role });
          } catch (error) {
            logger.error('Error initializing Firebase auth session:', { error: error.message });
            logAuthEvent('subscribe:session_error', { error: error.message });
            setUser(null);
            setRoles([]);
          }
        } else {
          logAuthEvent('subscribe:no_user', { message: 'User signed out or not authenticated' });
          if (refreshIntervalId) {
            clearInterval(refreshIntervalId);
            refreshIntervalId = null;
          }
          clearAuthTokens();
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('vh_auth_strategy');
          }
          setUser(null);
          setRoles([]);
        }
      });
    });

    return () => {
      activeAuthCallbacks = null;
      if (refreshIntervalId) clearInterval(refreshIntervalId);
      return unsubscribe();
    };
  },

  refreshProfileFromMe: async () => {
    if (!activeAuthCallbacks) return false;

    const token =
      typeof localStorage !== 'undefined' ? localStorage.getItem('vh_auth_token') : null;
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        logAuthEvent('refreshProfileFromMe:api_auth_me_error', { status: response.status });
        return false;
      }

      const data = await response.json();
      const apiUser = data?.data || null;
      if (!apiUser) return false;

      let storedUser = {};
      if (typeof localStorage !== 'undefined') {
        try {
          const raw = localStorage.getItem('vh_user_data');
          if (raw) storedUser = JSON.parse(raw);
        } catch (parseError) {
          logger.warn('[Firebase Auth] Could not parse stored user during profile refresh', {
            error: parseError?.message,
          });
        }
      }

      const role = apiUser?.role || storedUser?.role || 'gc';
      const roles = apiUser?.roles || storedUser?.roles || [role];
      const organizationId = deriveOrganizationId(apiUser);

      const mergedUser = {
        ...storedUser,
        ...apiUser,
        role,
        firebaseUid: storedUser?.firebaseUid || apiUser?.firebaseUid,
        ...(organizationId != null && { organizationId }),
      };

      if (mergedUser?.organization?.name && !mergedUser.organizationName) {
        mergedUser.organizationName = mergedUser.organization.name;
      }

      activeAuthCallbacks.setUser(mergedUser);
      activeAuthCallbacks.setRoles(roles);
      storeUserData({
        ...storedUser,
        ...apiUser,
        uid: apiUser?.uid || storedUser?.uid,
        firebaseUid: storedUser?.firebaseUid || apiUser?.firebaseUid,
        email: apiUser?.email || storedUser?.email,
        role,
        ...(organizationId != null && { organizationId }),
      });

      logAuthEvent('refreshProfileFromMe:complete', {
        email: mergedUser.email,
        organizationId,
      });
      return true;
    } catch (error) {
      logger.warn('[Firebase Auth] refreshProfileFromMe failed', { error: error.message });
      return false;
    }
  },
};

export default firebaseAdapter;
